import { useState, useCallback, useEffect } from 'react';
import {
    msdsCoverpageApi,
    MsdsExtractedData,
    MakBatValue,
    HStatementCheckResult,
    CoverpageFormData,
    SavedCoverpage,
    MsdsHStatement,
} from '../services/msdsCoverpageApi';

// Default Dolder AG supplier data
const DOLDER_DEFAULTS = {
    supplierName: 'Dolder AG',
    supplierAddress: 'Immengasse 9\nCH-4001 Basel',
    supplierPhone: '+41 61 326 66 00',
    supplierEmail: 'info@dolder.com',
    emergencyNumber: '145',
    section13Notes: 'Die Entsorgung muss in Übereinstimmung mit den geltenden regionalen, nationalen und lokalen Gesetzen und Vorschriften erfolgen. Verordnung über die Vermeidung und die Entsorgung von Abfällen (Abfallverordnung, VVEA) SR 814.600',
};

function createEmptyFormData(): CoverpageFormData {
    const today = new Date().toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return {
        coverpageDate: today,
        replacesVersionFrom: '',
        tradeName: '',
        usagePurpose: '',
        sdsVersion: '',
        ...DOLDER_DEFAULTS,
        section7Notes: '',
        makBatValues: [],
        section8PsaNotes: '',
        section15Notes: '',
        motherProtection: false,
        motherProtectionText: '',
        youthProtection: false,
        youthProtectionText: '',
        mengenschwelle: '',
    };
}

export function useMsdsCoverpage() {
    const [formData, setFormData] = useState<CoverpageFormData>(createEmptyFormData());
    const [extractedData, setExtractedData] = useState<MsdsExtractedData | null>(null);
    const [hStatements, setHStatements] = useState<MsdsHStatement[]>([]);
    const [hCheckResult, setHCheckResult] = useState<HStatementCheckResult | null>(null);
    const [savedCoverpages, setSavedCoverpages] = useState<SavedCoverpage[]>([]);
    const [currentId, setCurrentId] = useState<number | null>(null);
    const [saveName, setSaveName] = useState<string>('');
    const [saveNameManuallySet, setSaveNameManuallySet] = useState(false);

    const [isExtracting, setIsExtracting] = useState(false);
    const [isLookingUpMak, setIsLookingUpMak] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load saved coverpages on mount
    useEffect(() => {
        loadSavedCoverpages();
    }, []);

    const loadSavedCoverpages = useCallback(async () => {
        setIsLoadingList(true);
        try {
            const list = await msdsCoverpageApi.listCoverpages();
            setSavedCoverpages(list);
        } catch (err) {
            console.error('Failed to load coverpages:', err);
        } finally {
            setIsLoadingList(false);
        }
    }, []);

    const updateField = useCallback(<K extends keyof CoverpageFormData>(
        field: K,
        value: CoverpageFormData[K]
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Auto-sync save name from trade name unless manually overridden
        if (field === 'tradeName' && !saveNameManuallySet) {
            setSaveName(value as string);
        }
    }, [saveNameManuallySet]);

    const updateSaveName = useCallback((name: string) => {
        setSaveName(name);
        setSaveNameManuallySet(true);
    }, []);

    const updateMakBatValue = useCallback((index: number, field: keyof MakBatValue, value: string) => {
        setFormData(prev => {
            const updated = [...prev.makBatValues];
            if (field === 'makValue') {
                // Parse combined value like "50 ppm (152 mg/m³)"
                updated[index] = { ...updated[index], makValue: value, makUnit: '' };
            } else {
                updated[index] = { ...updated[index], [field]: value } as typeof updated[number];
            }
            return { ...prev, makBatValues: updated };
        });
    }, []);

    const extractPdf = useCallback(async (pdfBase64: string, fileName: string) => {
        setIsExtracting(true);
        setError(null);

        try {
            const result = await msdsCoverpageApi.extractPdf(pdfBase64, fileName);
            const data = result.extracted;
            setExtractedData(data);

            // Update form with extracted data
            const newTradeName = data.tradeName || '';
            setFormData(prev => ({
                ...prev,
                tradeName: newTradeName || prev.tradeName,
                usagePurpose: data.usagePurpose || prev.usagePurpose,
                sdsVersion: data.sdsDate || data.sdsVersion || prev.sdsVersion,
                section7Notes: data.handlingNotes || prev.section7Notes,
                section15Notes: data.legalReferences || prev.section15Notes,
            }));

            // Auto-set save name from extracted trade name
            if (newTradeName && !saveNameManuallySet) {
                setSaveName(newTradeName);
            }

            // Store H-statements
            const hStmts = data.hStatements || [];
            setHStatements(hStmts);

            // Trigger MAK lookup and H-check in parallel
            const ingredients = (data.ingredients || []).filter(i => i.casNumber);
            const hCodes = hStmts.map(h => h.code);

            const [, hResult] = await Promise.allSettled([
                ingredients.length > 0
                    ? lookupMakValues(ingredients)
                    : Promise.resolve([]),
                hCodes.length > 0
                    ? msdsCoverpageApi.checkHStatements(hCodes)
                    : Promise.resolve(null),
            ]);

            if (hResult.status === 'fulfilled' && hResult.value) {
                setHCheckResult(hResult.value);
                setFormData(prev => ({
                    ...prev,
                    motherProtection: hResult.value!.motherProtection,
                    motherProtectionText: hResult.value!.motherProtectionText || '',
                    youthProtection: hResult.value!.youthProtection,
                    youthProtectionText: hResult.value!.youthProtectionText || '',
                }));
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to extract PDF data');
        } finally {
            setIsExtracting(false);
        }
    }, []);

    const lookupMakValues = useCallback(async (
        ingredients: Array<{ casNumber: string; name: string }>
    ): Promise<MakBatValue[]> => {
        setIsLookingUpMak(true);
        try {
            const results = await msdsCoverpageApi.lookupMak(ingredients);
            setFormData(prev => ({
                ...prev,
                makBatValues: results.map(r => ({
                    substance: r.substance,
                    casNumber: r.casNumber,
                    makValue: r.makValue,
                    makUnit: r.makUnit,
                    notation: r.notation,
                })),
            }));
            return results;
        } catch (err) {
            console.error('MAK lookup failed:', err);
            return [];
        } finally {
            setIsLookingUpMak(false);
        }
    }, []);

    const saveCoverpage = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        try {
            const result = await msdsCoverpageApi.saveCoverpage({
                id: currentId || undefined,
                tradeName: saveName || formData.tradeName,
                sdsVersion: formData.sdsVersion,
                originalSupplier: extractedData?.originalSupplier?.name || undefined,
                formData,
            });
            setCurrentId(result.id);
            await loadSavedCoverpages();
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save');
            throw err;
        } finally {
            setIsSaving(false);
        }
    }, [formData, currentId, extractedData, loadSavedCoverpages]);

    const generateDocument = useCallback(async (format: 'docx' | 'pdf') => {
        setIsGenerating(true);
        setError(null);
        try {
            const blob = await msdsCoverpageApi.generateDocument(formData, format);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Deckblatt_${formData.tradeName.replace(/[^a-zA-Z0-9äöüÄÖÜ]/g, '_')}.${format}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to generate document');
        } finally {
            setIsGenerating(false);
        }
    }, [formData]);

    const loadCoverpage = useCallback(async (id: number) => {
        setError(null);
        try {
            const detail = await msdsCoverpageApi.getCoverpage(id);
            setCurrentId(detail.Id);
            setFormData(detail.FormData);
            setSaveName(detail.TradeName);
            setSaveNameManuallySet(detail.TradeName !== detail.FormData.tradeName);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to load cover page';
            setError(msg);
            throw err;
        }
    }, []);

    const resetForm = useCallback(() => {
        setFormData(createEmptyFormData());
        setExtractedData(null);
        setHStatements([]);
        setHCheckResult(null);
        setCurrentId(null);
        setSaveName('');
        setSaveNameManuallySet(false);
        setError(null);
    }, []);

    return {
        formData,
        extractedData,
        hStatements,
        hCheckResult,
        savedCoverpages,
        currentId,
        saveName,
        isExtracting,
        isLookingUpMak,
        isSaving,
        isGenerating,
        isLoadingList,
        error,
        updateField,
        updateSaveName,
        updateMakBatValue,
        extractPdf,
        saveCoverpage,
        generateDocument,
        loadCoverpage,
        resetForm,
    };
}
